import React, { useEffect, useState } from "react";
import { MdEdit } from "react-icons/md";
import { MdDelete } from "react-icons/md";
import { useInteriorDesignCategory } from "./useHook";
import Swal from "sweetalert2";
import InteriorDesignCategoryModal from "../../Components/Modals/InteriorDesignCategory/InteriorDesignCategoryModal";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";
import { ImageWithFallback } from "../../Utils/loader";
import Loader from "../../Utils/loader";

const InteriorDesignCategory = () => {
    const {
        addInteriorDesignCategory,
        getInteriorDesignCategoriesWithPagination,
        updateInteriorDesignCategory,
        deleteInteriorDesignCategory,
        toggleInteriorDesignCategoryStatus
    } = useInteriorDesignCategory();

    const [categories, setCategories] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Search state
    const [searchName, setSearchName] = useState("");
    const [searchStatus, setSearchStatus] = useState("");

    useEffect(() => {
        fetchCategories();
    }, [currentPage]);

    const fetchCategories = async () => {
        try {
            setIsLoading(true);
            const offset = (currentPage - 1) * limit;
            const response = await getInteriorDesignCategoriesWithPagination(offset, limit, searchName, searchStatus);
            if (response && response.data) {
                setCategories(response.data);
                if (response.pagination) {
                    setTotal(response.pagination.totalItems);
                    setTotalPages(response.pagination.totalPages);
                }
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            Swal.fire("Error", "Failed to fetch categories", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (newOffset) => {
        const newPage = Math.floor(newOffset / limit) + 1;
        setCurrentPage(newPage);
    };

    const handleSearch = () => {
        setCurrentPage(1); // Reset to first page when searching
        fetchCategories();
    };

    const handleClearSearch = async () => {
        setSearchName("");
        setSearchStatus("");
        setCurrentPage(1);
        // Fetch with cleared values immediately
        setIsLoading(true);
        try {
            const offset = 0;
            const response = await getInteriorDesignCategoriesWithPagination(offset, limit, "", "");
            if (response && response.data) {
                setCategories(response.data);
                if (response.pagination) {
                    setTotal(response.pagination.totalItems);
                    setTotalPages(response.pagination.totalPages);
                }
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            Swal.fire("Error", "Failed to fetch categories", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
    };

    const handleSaveCategory = async (categoryData, id = null) => {
        setIsLoading(true);
        try {
            let response;
            if (id) {
                response = await updateInteriorDesignCategory(id, categoryData);
            } else {
                response = await addInteriorDesignCategory(categoryData);
            }

            if (response) {
                await fetchCategories();
                toggleModal();
                Swal.fire("Success", `Category ${id ? 'updated' : 'added'} successfully!`, "success");
            }
        } catch (error) {
            console.error("Error in handleSaveCategory:", error);
            Swal.fire("Error", error.message || "Failed to save category.", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditCategory = (category) => {
        setCurrentCategory(category);
        toggleModal();
    };

    const handleDeleteCategory = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                const response = await deleteInteriorDesignCategory(id);
                if (response) {
                    await fetchCategories();
                    Swal.fire("Deleted!", "Category has been deleted.", "success");
                }
            } catch (error) {
                Swal.fire("Error", error.message || "Failed to delete category.", "error");
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            const response = await toggleInteriorDesignCategoryStatus(id, newStatus);

            if (response) {
                setCategories((prevCategories) =>
                    prevCategories.map((category) =>
                        category.id === id ? { ...category, status: newStatus } : category
                    )
                );

                Swal.fire({
                    title: "Success",
                    text: `Category status has been ${newStatus ? "activated" : "deactivated"} successfully!`,
                    icon: "success",
                    timer: 1000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error.message || "Failed to update category status.",
                icon: "error",
                timer: 2000,
                timerProgressBar: true,
                showConfirmButton: false,
            });
        }
    };

    if (isLoading) {
        return <Loader message="Loading interior design categories..." />;
    }

    return (
        <div>
            <div className="bg-gray-100 p-4 flex justify-between">
                <h2 className="text-2xl font-bold">Interior Design Categories</h2>
                <button
                    className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
                    onClick={() => {
                        setCurrentCategory(null);
                        toggleModal();
                    }}
                >
                    Add Interior Design Category
                </button>
            </div>

            <div className="mt-4">
                <div className="bg-white p-4 rounded-md shadow-md mb-6 flex gap-4 mt-4">
                    <input
                        type="text"
                        className="w-[40%] flex-grow px-4 py-2 border rounded-lg"
                        placeholder="Search by category name"
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                    />
                    <select
                        className="w-[20%] px-4 py-2 border rounded-lg"
                        value={searchStatus}
                        onChange={(e) => setSearchStatus(e.target.value)}
                    >
                        <option value="" className="opacity-25">
                            Select Status
                        </option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                    <button
                        className="w-full sm:w-[45%] md:w-60 bg-blue-500 text-white px-4 py-2 rounded-md"
                        onClick={handleSearch}
                    >
                        Search
                    </button>
                    <button
                        className="w-full sm:w-[45%] md:w-60 bg-primary-dark text-white px-4 py-2 rounded-md"
                        onClick={handleClearSearch}
                    >
                        Reset
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto mt-6">
                <table className="min-w-full table-auto bg-white shadow-md rounded-lg overflow-hidden">
                    <thead>
                        <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
                            <th className="px-6 py-3 text-left text-sm font-semibold">
                                Sr. No.
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-semibold">
                                Image
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-semibold">
                                Category Name
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-semibold">
                                Description
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-semibold">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-semibold">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(categories) && categories.length > 0 ? (
                            categories.map((category, index) => (
                                <tr
                                    key={category.id}
                                    className={`${index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                                        } hover:bg-gray-200 transition-colors duration-300`}
                                >
                                    <td className="px-4 py-2">{(currentPage - 1) * limit + index + 1}</td>
                                    <td className="px-4 py-2">
                                        {category.image_url ? (
                                            <ImageWithFallback
                                                src={category.image_url}
                                                alt={category.name}
                                                className="w-16 h-16 object-cover rounded-lg"
                                            />
                                        ) : (
                                            <div className="w-16 h-16 bg-gray-200 rounded-md flex items-center justify-center">
                                                <span className="text-gray-500 text-xs">No Image</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 font-medium">{category.name}</td>
                                    <td className="px-4 py-2 max-w-xs truncate">
                                        {category.description}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div
                                            className={`relative inline-block w-16 h-8 rounded-full transition-colors duration-300 cursor-pointer ${category.status ? "bg-primary-dark" : "bg-[#cccccc]"
                                                }`}
                                            onClick={() =>
                                                handleToggleStatus(category.id, category.status)
                                            }
                                        >
                                            <div
                                                className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transform transition-transform duration-300 ${category.status ? "translate-x-8" : ""
                                                    }`}
                                            ></div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 flex space-x-2">
                                        <button
                                            className="bg-primary-dark text-white py-2 px-3 rounded-lg shadow-md flex items-center"
                                            onClick={() => handleEditCategory(category)}
                                            title="Edit"
                                        >
                                            <MdEdit />
                                        </button>
                                        <button
                                            className="bg-red-500 text-white py-2 px-3 rounded-lg shadow-md flex items-center"
                                            onClick={() => handleDeleteCategory(category.id)}
                                            title="Delete"
                                        >
                                            <MdDelete />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center py-6 text-gray-500">
                                    No interior design categories found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-6">
                    <GlobalPagination
                        offset={(currentPage - 1) * limit}
                        limit={limit}
                        total={total}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}

            <InteriorDesignCategoryModal
                modalOpen={modalOpen}
                toggleModal={toggleModal}
                category={currentCategory}
                handleSaveCategory={handleSaveCategory}
            />
        </div>
    );
};

export default InteriorDesignCategory;